import { ui } from "@/components/ui";
import {
  createUniqueShockwaveOptions,
  toFilteredUniqueShockwaveOptions,
} from "@/features/shockwave/utils/shockwaveOptions";
import { services as s } from "@/services";
import { Copy, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { formComponents as fc } from "../../../components/form";
import GroupedFields from "../../../components/form/GroupedFields";
import { cn } from "../../../lib/utils";
import { sideOptions } from "../../events/utils/sideOptions";
import useShockwaveDetailsOptions from "../hooks/useShockwaveDetailsOptions";
import ShockwaveLocationForm from "./ShockwaveLocationForm";

// Define the schema type for shockwave detail
export interface ShockwaveDetailSchemaT {
  shockwave_detail_id: string;
  shockwave_id: string;
  side?: string | null;
  orthopaedic_area?: string | null;
  site?: string | null;
  subsite?: string | null;
  shockwave_type?: "focused" | "radial" | null;
  impulses?: string | null;
  dose?: string | null;
  unit?: string | null;
}

// Define the form schema type for the parent form
export interface ShockwaveFormSchemaT {
  shockwave_details: ShockwaveDetailSchemaT[];
}

interface Props {
  shockwaveId: string;
  className?: string;
}

interface OptionT {
  label: string;
  value: string;
}

const ShockwaveDetailItem = (
  { index, control, setValue, remove, append, shockwaveId, formValues },
) => {
  const { shockwaveLocations, shockwaveContents } =
    useShockwaveDetailsOptions();
  const safeShockwaveContents = shockwaveContents || [];

  const shockwaveType = useWatch({
    control,
    name: `shockwave_details.${index}.shockwave_type`,
  });

  const selectedImpulses = useWatch({
    control,
    name: `shockwave_details.${index}.impulses`,
  });

  const selectedDose = useWatch({
    control,
    name: `shockwave_details.${index}.dose`,
  });

  const isMounted = useRef(false);

  useEffect(() => {
    if (isMounted.current) {
      if (shockwaveType) {
        const contentForType = safeShockwaveContents.find(
          (c) => c.shockwave_type === shockwaveType,
        );
        if (contentForType) {
          setValue(
            `shockwave_details.${index}.unit`,
            contentForType.unit,
          );
        }
        // Reset dependent fields when type changes
        setValue(`shockwave_details.${index}.impulses`, "");
        setValue(`shockwave_details.${index}.dose`, "");
      }
    } else {
      isMounted.current = true;
    }
  }, [shockwaveType, index, setValue, safeShockwaveContents]);

  useEffect(() => {
    if (shockwaveType && selectedImpulses && selectedDose) {
      const contentForSelection = safeShockwaveContents.find(
        (c) =>
          c.shockwave_type === shockwaveType &&
          c.impulses.toString() === selectedImpulses &&
          c.dose.toString() === selectedDose,
      );
      if (contentForSelection) {
        setValue(
          `shockwave_details.${index}.unit`,
          contentForSelection.unit,
        );
      }
    }
  }, [
    shockwaveType,
    selectedImpulses,
    selectedDose,
    index,
    setValue,
    safeShockwaveContents,
  ]);

  const impulseOptions: OptionT[] = useMemo(() => {
    if (!shockwaveType) return [];
    return [
      ...new Set(
        safeShockwaveContents
          .filter((c) => c.shockwave_type === shockwaveType)
          .map((c) => c.impulses.toString()),
      ),
    ].map((value) => ({ label: value, value }));
  }, [shockwaveType, safeShockwaveContents]);

  useEffect(() => {
    if (impulseOptions && impulseOptions.length === 1) {
      setValue(
        `shockwave_details.${index}.impulses`,
        impulseOptions[0]?.value,
      );
    }
  }, [impulseOptions, setValue, index]);

  const doseOptions: OptionT[] = useMemo(() => {
    if (!shockwaveType || !selectedImpulses) return [];
    return [
      ...new Set(
        safeShockwaveContents
          .filter(
            (c) =>
              c.shockwave_type === shockwaveType &&
              c.impulses.toString() === selectedImpulses,
          )
          .map((c) => c.dose.toString()),
      ),
    ].map((value) => ({ label: value, value }));
  }, [shockwaveType, selectedImpulses, safeShockwaveContents]);

  useEffect(() => {
    if (doseOptions && doseOptions.length === 1) {
      setValue(`shockwave_details.${index}.dose`, doseOptions[0]?.value);
    }
  }, [doseOptions, setValue, index]);

  const unitOptions: OptionT[] = useMemo(() => {
    if (!shockwaveType || !selectedImpulses || !selectedDose) return [];
    return [
      ...new Set(
        safeShockwaveContents
          .filter(
            (c) =>
              c.shockwave_type === shockwaveType &&
              c.impulses.toString() === selectedImpulses &&
              c.dose.toString() === selectedDose,
          )
          .map((c) => c.unit),
      ),
    ].map((value) => ({ label: value, value }));
  }, [shockwaveType, selectedImpulses, selectedDose, safeShockwaveContents]);

  useEffect(() => {
    if (unitOptions && unitOptions.length === 1) {
      setValue(`shockwave_details.${index}.unit`, unitOptions[0]?.value);
    }
  }, [unitOptions, setValue, index]);

  return (
    <div
      className="flex flex-col gap-y-4 bg-indigo-50 dark:bg-slate-900 shadow-md p-4 border border-slate-300 rounded-lg"
      key={index}
    >
      <div className="flex flex-row justify-between items-center">
        <h3 className="text-slate-900 dark:text-slate-100">
          Shockwave {index + 1}
        </h3>
        <div className="flex flex-row flex-wrap mt-2">
          <ui.Button
            size="icon"
            type="button"
            variant="ghost"
            onClick={() => remove(index)}
            disabled={formValues.length === 1}
          >
            <Trash2 size={20} />
          </ui.Button>
          <ui.Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() =>
              append({
                shockwave_id: shockwaveId,
                shockwave_detail_id: s.uuidv4(),
                side: formValues[index]?.side || "",
                orthopaedic_area: formValues[index]?.orthopaedic_area || "",
                site: formValues[index]?.site || "",
                subsite: formValues[index]?.subsite || "",
                shockwave_type: formValues[index]?.shockwave_type || null,
                impulses: formValues[index]?.impulses || "",
                dose: formValues[index]?.dose || "",
                unit: formValues[index]?.unit || "",
              })}
          >
            <Copy size={20} />
          </ui.Button>
        </div>
      </div>
      <div className="flex md:flex-row flex-col gap-x-2 gap-y-4">
        <fc.Select
          name={`shockwave_details.${index}.side`}
          label="Side"
          options={sideOptions}
          clearable={true}
        />
        <fc.ComboBox
          name={`shockwave_details.${index}.orthopaedic_area`}
          label="Anatomic Area"
          options={createUniqueShockwaveOptions(
            shockwaveLocations || [],
            "orthopaedic_area",
          )}
          addOption={<ShockwaveLocationForm />}
          clearable={true}
        />
      </div>
      <GroupedFields>
        <fc.ComboBox
          name={`shockwave_details.${index}.site`}
          label="Shockwave Site"
          options={toFilteredUniqueShockwaveOptions(
            shockwaveLocations || [],
            "site",
            "orthopaedic_area",
            formValues[index]?.orthopaedic_area || "",
          )}
          addOption={<ShockwaveLocationForm />}
          clearable={true}
        />
        <fc.ComboBox
          name={`shockwave_details.${index}.subsite`}
          label="Shockwave Subsite"
          options={toFilteredUniqueShockwaveOptions(
            shockwaveLocations || [],
            "subsite",
            "site",
            formValues[index]?.site || "",
          )}
          addOption={<ShockwaveLocationForm />}
          clearable={true}
        />
      </GroupedFields>
      <GroupedFields>
        <fc.ComboBox
          name={`shockwave_details.${index}.shockwave_type`}
          label="Shockwave Type"
          options={[
            { label: "Focused", value: "focused" },
            { label: "Radial", value: "radial" },
          ]}
          clearable={true}
        />
        <fc.ComboBox
          name={`shockwave_details.${index}.impulses`}
          label="Impulses"
          options={impulseOptions}
          clearable={true}
        />
      </GroupedFields>
      <GroupedFields>
        <>
          <fc.ComboBox
            name={`shockwave_details.${index}.dose`}
            label="Dose"
            options={doseOptions}
            clearable={true}
          />
          <fc.ComboBox
            name={`shockwave_details.${index}.unit`}
            label="Unit"
            options={unitOptions}
            clearable={true}
          />
        </>
      </GroupedFields>
    </div>
  );
};

const ShockwaveDetailsFormFields = ({ shockwaveId, className }: Props) => {
  const { control, setValue, watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "shockwave_details",
  });

  const formValues = watch("shockwave_details") || [];

  useEffect(() => {
    if (!fields || fields.length === 0) {
      append({
        shockwave_id: shockwaveId,
        shockwave_detail_id: s.uuidv4(),
        side: "",
        orthopaedic_area: "",
        site: "",
        subsite: "",
        shockwave_type: null,
        impulses: "",
        dose: "",
        unit: "",
      });
    }
  }, [fields, append, shockwaveId]);

  if (!fields || fields.length === 0) {
    return <div>Loading shockwave details form...</div>;
  }

  return (
    <div className={cn("w-full space-y-4 text-lg", className)}>
      {fields.map((field, index) => (
        <ShockwaveDetailItem
          key={field.id}
          index={index}
          control={control}
          setValue={setValue}
          remove={remove}
          append={append}
          shockwaveId={shockwaveId}
          formValues={formValues}
        />
      ))}
    </div>
  );
};

export default ShockwaveDetailsFormFields;
